import React, { useState, useEffect } from "react";
import { Dayjs } from "dayjs";
import TimePicker from "react-time-picker";
import { cn } from "../../lib/utils";
import { Button } from "./button";
import { Popover, PopoverContent, PopoverTrigger } from "./popover";

import "react-time-picker/dist/TimePicker.css";
import "react-clock/dist/Clock.css";

// Locale-aware time formatting
const formatTimeLocale = (date: Date | Dayjs | null | undefined): string => {
  if (!date) return "--:--";
  const d = date instanceof Date ? date : date.toDate();
  return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
};

// Detect if locale uses 12-hour format (has AM/PM)
const uses12HourFormat = (() => {
  const formatted = new Date().toLocaleTimeString([], { hour: 'numeric' });
  return /AM|PM/i.test(formatted);
})();

// Time format based on locale
const timeFormat = uses12HourFormat ? "h:mm a" : "HH:mm";

interface SingleTimePickerProps {
  value?: Dayjs | null;
  onChange?: (value: Dayjs | null) => void;
  className?: string;
  disabled?: boolean;
}

export const SingleTimePicker: React.FC<SingleTimePickerProps> = ({
  value,
  onChange,
  className,
  disabled = false,
}) => {
  const [open, setOpen] = useState(false);
  const [time, setTime] = useState<string | null>(null);

  useEffect(() => {
    if (value) setTime(value.format("HH:mm"));
  }, [value]);

  const handleApply = () => {
    if (time && value) {
      const [hours, minutes] = time.split(":").map(Number);
      const newValue = value.hour(hours).minute(minutes).second(0);
      onChange?.(newValue);
    }
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={disabled}
          className={cn(
            "inline-flex items-center justify-center gap-1 px-3 py-1.5 rounded-md border border-input bg-background text-sm font-medium tabular-nums",
            "hover:bg-accent hover:text-accent-foreground transition-colors",
            "focus:outline-none focus:ring-1 focus:ring-border",
            disabled && "opacity-50 cursor-not-allowed",
            className
          )}
        >
          {formatTimeLocale(value)}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-3" align="start">
        <div className="space-y-3">
          <div className="time-input-wrapper">
            <TimePicker
              value={time}
              onChange={(val) => setTime(val as string | null)}
              format={timeFormat}
              disableClock
              clearIcon={null}
              clockIcon={null}
            />
          </div>

          <div className="flex justify-end gap-2">
            <Button variant="link" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button variant="segment" onClick={handleApply}>
              Apply
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};
